/**
 * API client utilities for making requests to backend
 */

import { supabase } from '@/lib/supabase'
import { getActiveLanguage } from '@/i18n'

/** An HTTP error from the API, carrying the status separately from the message. */
export class ApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/**
 * Get the API URL for a path
 * Uses VITE_API_URL in production, relative paths in development (Vite proxy).
 */
export function getApiUrl(path: string): string {
  const baseUrl = import.meta.env.VITE_API_URL || ''
  return `${baseUrl}${path}`
}

/**
 * Get auth headers with JWT token from Supabase session
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (session?.access_token) {
    return { Authorization: `Bearer ${session.access_token}` }
  }
  return {}
}

/**
 * Wrapper around fetch with consistent error handling and JWT injection
 */
export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const url = getApiUrl(path)
  const authHeaders = await getAuthHeaders()

  console.log(`[API] ${options?.method || 'GET'} ${url}`)

  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        // Tell the API which UI language to localize display copy in. Mirrors
        // the active i18next language (see src/i18n).
        'Accept-Language': getActiveLanguage(),
        ...authHeaders,
        ...options?.headers,
      },
    })

    console.log(`[API] Response ${response.status} ${response.statusText}`)

    // Handle 401 - redirect to login
    if (response.status === 401) {
      console.warn('[API] Unauthorized - redirecting to login')
      window.location.href = '/login'
      throw new Error('Unauthorized')
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const message =
        errorData.detail || errorData.message || errorData.error || response.statusText

      if (response.status >= 500) {
        console.error(`[API] Error response:`, errorData)
      }
      // The message is rendered straight into the UI, so it must read as a sentence to
      // the person holding the mouse. It used to be prefixed with the status code,
      // which put "403: Access denied" on screen: an HTTP number is for the console,
      // not for a business owner. The status is still attached for callers that branch
      // on it, and still logged above.
      throw new ApiError(message, response.status)
    }

    // Handle 204 No Content or empty responses
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      console.log(`[API] Success`)
      return {} as T
    }

    const data = await response.json()
    return data
  } catch (error) {
    if (error instanceof Error && !error.message.match(/^[45]\d\d:/)) {
      console.error(`[API] Fetch failed for ${url}:`, error)
    }
    throw error
  }
}
